import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'

import LegalLink from '../components/LegalLink'
import ReilmLogo from '../components/ReilmLogo'
import { reilmAPI } from '../services/apiClient'
import { useAuth } from '../features/auth/AuthContext'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)

const PLANS = {
  starter: {
    name: 'Starter',
    monthlyList: 19,
    annualList: 228,
    annualDue: 204,
    annualDiscountPct: 10,
    features: ['20 Properties per Month', 'Top 10 ARV Comps', '2 Custom Templates'],
    supportsFounding: false,
  },
  pro: {
    name: 'Pro',
    monthlyList: 79,
    annualList: 948,
    annualDue: 948,
    annualDiscountPct: 0,
    // Must match the pricing table (PricingFeature.jsx): Pro is 250/month, not unlimited.
    features: ['250 Properties per Month', 'All ARV Comps', 'Unlimited Custom Templates'],
    supportsFounding: true,
  },
}

// Server codes that mean "you already have a subscription" rather than "something broke".
// Plan changes are SOW 2, so a paying customer clicking Change Plan reaches this on purpose.
const ALREADY_SUBSCRIBED_CODES = ['SUBSCRIPTION_EXISTS', 'SUBSCRIPTION_PAST_DUE']

const formatDate = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

const money = (n) => `$${n.toFixed(2)} USD`

// D4.3: smaller checkmarks in the summary card.
const CheckIcon = () => (
  <div className="w-[20px] h-[20px] rounded-full bg-brand-primary flex items-center justify-center shrink-0">
    <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
      <path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
)

// D4.3: the annual option names its saving — "Annual (Save 10%)" / "Annual (Save 50%)".
// The percentage is derived from the discount actually in effect rather than written in,
// so the label cannot end up promising a saving the total does not show.
const BillingToggle = ({ billing, setBilling, annualSavingPct }) => (
  <div className="flex items-center gap-1 p-1 rounded-full bg-white/10 w-fit">
    {['monthly', 'annual'].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => setBilling(opt)}
        className={`px-4 h-[32px] text-[13px] font-semibold rounded-full transition-colors ${
          billing === opt ? 'bg-brand-primary text-white' : 'text-white/70 hover:text-white'
        }`}
      >
        {opt === 'monthly'
          ? 'Monthly'
          : annualSavingPct > 0
            ? `Annual (Save ${annualSavingPct}%)`
            : 'Annual'}
      </button>
    ))}
  </div>
)

const PaymentInner = ({ plan, planId, billing, setBilling, annualSavingPct, foundingEligible, renewsOn, dueToday, discount, discountPct, listPrice, intentType }) => {
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setSubmitError(null)

    const confirmFn = intentType === 'setup' ? stripe.confirmSetup : stripe.confirmPayment
    const { error } = await confirmFn({
      elements,
      confirmParams: { return_url: `${window.location.origin}/#/payment/success?plan=${planId}&billing=${billing}` },
      redirect: 'if_required',
    })

    if (error) {
      setSubmitError(error.message || 'Payment confirmation failed')
      setSubmitting(false)
    } else {
      navigate(`/payment/success?plan=${planId}&billing=${billing}`)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex-1 max-w-canvas w-full mx-auto px-6 md:px-[120px] pt-[40px] pb-[80px]">
      {/* D4.3: "Go Back" used to live inside the left column, so the summary card on the
          right started level with a small text link rather than with the heading. It sits
          above both columns now, and the two tops line up on the H1. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-brand-primary text-[16px] font-semibold hover:text-brand-hover"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M13 5l-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Go Back
      </button>

      <div className="mt-[16px] md:mt-[24px] grid grid-cols-1 md:grid-cols-2 gap-[40px] md:gap-[80px] items-start">
      {/* LEFT: heading + card fields */}
      <div>
        <h1 className="text-[32px] leading-[40px] md:text-[48px] md:leading-[56px] font-extrabold text-brand-dark">
          Review your subscription
        </h1>

        {/* D4.2.1: the "Card Details" label is gone, and D4.2.5: so is the wrapper's own
            rounded border — it drew a second box around Stripe's field borders. */}
        <div className="mt-[40px]">
          <div>
            <PaymentElement
              options={{
                layout: 'accordion',
                defaultValues: { billingDetails: { address: { country: 'US' } } },
              }}
            />
          </div>
          {submitError && <p className="mt-3 text-[13px] text-red-500">{submitError}</p>}
        </div>
      </div>

      {/* RIGHT: dark summary card */}
      <div>
        <div className="relative rounded-[24px] overflow-hidden p-8 text-white" style={{ backgroundColor: '#17270D' }}>
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top_right,_rgba(76,188,24,0.5),_transparent_55%)]" />

          <div className="relative">
            <div className="mb-4">
              <BillingToggle billing={billing} setBilling={setBilling} annualSavingPct={annualSavingPct} />
            </div>
            {/* Header row: Pro | $79/monthly */}
            <div className="flex justify-between items-baseline">
              <h3 className="text-[32px] font-extrabold leading-none">{plan.name}</h3>
              <div className="text-right">
                <span className="text-[32px] font-extrabold">${listPrice}</span>
                <span className="text-[16px] font-normal text-white/70">
                  {billing === 'annual' ? '/yearly' : '/monthly'}
                </span>
              </div>
            </div>

            {/* Discount — per prices-before-after: shown whenever list price and due differ,
                so the customer can see where the saving came from instead of an unexplained total. */}
            {discount > 0 && (
              <div className="mt-5 flex justify-between items-center">
                <span className="text-[16px] font-semibold text-white">
                  Discount ({discountPct}%)
                </span>
                <span className="text-[16px] font-semibold text-white">{money(discount)}</span>
              </div>
            )}

            {/* Due today */}
            <div className={`${discount > 0 ? 'mt-2' : 'mt-5'} flex justify-between items-center`}>
              <span className="text-[16px] font-semibold text-white">Due today</span>
              <span className="text-[16px] font-semibold text-white">{money(dueToday)}</span>
            </div>

            {/* Renews on */}
            <div className="mt-2 flex justify-between items-center">
              <span className="text-[14px] text-white/60">Renews on</span>
              <span className="text-[14px] text-white/60">{formatDate(renewsOn)}</span>
            </div>

            {/* D4.3: the small Founding Promo switch is gone — the large Monthly/Annual
                toggle now carries the saving in its own label ("Annual (Save 50%)"), so
                the switch was a second control for the same decision. The promo applies
                to Pro annual on its own, which is what the pricing card already
                advertises. The line below states it rather than offering it. */}
            {foundingEligible && (
              <p className="mt-6 text-[14px] text-white/90">
                Founding Promo Applied — Saving 50% for Life
              </p>
            )}

            <div className="mt-6 border-t border-white/10" />

            {/* Feature bullets */}
            <div className="mt-6 flex flex-col gap-3">
              {plan.features.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <CheckIcon />
                  <span className="text-[15px] text-white">{f}</span>
                </div>
              ))}
            </div>

            {/* T&C */}
            <p className="mt-8 text-center text-[13px] text-white/70">
              By subscribing, you agree to the{' '}
              <LegalLink to="/terms" className="underline">Terms</LegalLink> and{' '}
              <LegalLink to="/privacy" className="underline">Privacy Policy</LegalLink>.
            </p>

            {/* Subscribe */}
            <button
              type="submit"
              disabled={!stripe || submitting}
              className="mt-4 w-full h-[56px] rounded-full bg-brand-primary hover:bg-brand-hover disabled:opacity-60 text-white text-[16px] font-bold transition-colors"
            >
              {submitting ? 'Processing…' : 'Subscribe'}
            </button>

            <p className="mt-3 text-center text-[12px] text-white/50">Secure payment via Stripe</p>
          </div>
        </div>
      </div>
      </div>
    </form>
  )
}

const Payment = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { isAuthenticated, user } = useAuth()

  const planId = params.get('plan') === 'starter' ? 'starter' : 'pro'
  const initialBilling = params.get('billing') === 'annual' ? 'annual' : 'monthly'

  const [billing, setBilling] = useState(initialBilling)
  const [clientSecret, setClientSecret] = useState(null)
  const [intentType, setIntentType] = useState(null)
  const [initError, setInitError] = useState(null)
  const [initErrorCode, setInitErrorCode] = useState(null)
  const [initing, setIniting] = useState(true)

  const plan = PLANS[planId]

  // Founding promo is Pro **annual** only — the banner promises it to "the first 100 Pro
  // annual subscribers". D4.3 removed the switch that used to gate it, so eligibility and
  // application are now the same thing: pick Pro annual and the promo is on. This matches
  // what the pricing card advertises, which always sent founding=1 for that combination.
  const foundingEligible = Boolean(plan.supportsFounding) && billing === 'annual'
  const foundingActive = foundingEligible

  const renewsOn = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + (billing === 'annual' ? 365 : 30))
    return d
  }, [billing])

  const listPrice = billing === 'annual' ? plan.annualList : plan.monthlyList
  const dueToday = useMemo(() => {
    if (foundingActive) return listPrice * 0.5
    if (billing === 'annual') return plan.annualDue
    return listPrice
  }, [billing, listPrice, plan, foundingActive])

  // Derived, not hardcoded, so the row can never disagree with the total.
  const discount = Math.max(0, listPrice - dueToday)
  // Founding is a flat 50%; the annual saving uses the plan's advertised percentage
  // (Starter is billed as "Save 10%" even though $24 off $228 is nearer 10.5%).
  const discountPct = foundingActive ? 50 : plan.annualDiscountPct
  // What the annual option is worth *if selected* — the toggle label needs this while the
  // customer is still on monthly, where `discountPct` is necessarily 0.
  const annualSavingPct = plan.supportsFounding ? 50 : plan.annualDiscountPct

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/payment?plan=${planId}&billing=${billing}` } })
      return
    }
    let cancelled = false
    setIniting(true)
    setInitError(null)
    setInitErrorCode(null)
    setClientSecret(null)

    reilmAPI
      .createSubscription({ plan: planId, billing, applyFounding: foundingActive })
      .then((res) => {
        if (cancelled) return
        const cs = res.data?.data?.clientSecret
        const t = res.data?.data?.type
        if (!cs) throw new Error('No client secret returned')
        setClientSecret(cs)
        setIntentType(t || 'payment')
      })
      .catch((err) => {
        if (cancelled) return
        setInitError(err?.response?.data?.error?.message || err.message || 'Unable to initialize checkout')
        setInitErrorCode(err?.response?.data?.error?.code || null)
      })
      .finally(() => !cancelled && setIniting(false))

    return () => {
      cancelled = true
    }
  }, [planId, billing, foundingActive, isAuthenticated, navigate, user])

  return (
    <div className="min-h-screen bg-brand-pageFill flex flex-col">
      {/* D4.1: bottom stroke on the checkout navbar. Set #4 Checkout 1: 72px mobile / 90px desktop. */}
      <header className="h-[72px] md:h-[90px] px-6 md:px-[120px] flex items-center border-b border-brand-muted">
        {/* 14.08 followup, Other Notes 1: was a static <img>, which can't reach the SVG's own
            fills for a hover — same reason ReilmLogo exists (see that file's own comment).
            Swapped for the inline component the shared public Navbar already uses; `group` on
            the link is what lets its `group-hover:fill-[...]` resolve. */}
        <Link to="/" className="flex items-center group" aria-label="Arvance home">
          <ReilmLogo className="w-[131px] h-[28px] md:w-[168px] md:h-[36px]" />
        </Link>
      </header>

      {initError ? (
        // A subscriber who already pays lands here by design — the server refuses to open a
        // second subscription (409). That is information, not a failure, and it needs a way
        // out: before this the only exit from the error state was the logo.
        <div className="flex-1 flex items-start justify-center px-6 pt-[32px] md:pt-[64px]">
          <div className="w-full max-w-[440px] flex flex-col items-center gap-6 text-center">
            <p
              className={`text-[15px] leading-[24px] ${
                ALREADY_SUBSCRIBED_CODES.includes(initErrorCode)
                  ? 'text-brand-dark/85'
                  : 'text-red-500'
              }`}
            >
              {initError}
            </p>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="h-[56px] px-8 rounded-full bg-brand-primary hover:bg-brand-hover text-white text-[16px] font-bold transition-colors"
            >
              Back to Pricing
            </button>
          </div>
        </div>
      ) : !clientSecret ? (
        <div className="flex-1 flex items-center justify-center px-6">
          <p className="text-brand-dark/60 text-[15px]">{initing ? 'Preparing checkout…' : 'Loading…'}</p>
        </div>
      ) : (
        <Elements
          key={clientSecret}
          stripe={stripePromise}
          options={{
            clientSecret,
            // D4.2.4: `fontFamily: 'Figtree'` in the variables below was never going to
            // work on its own — the fields render in a cross-origin iframe that cannot see
            // this page's stylesheet, so Stripe fell back to system-ui whatever the
            // variable said. The face has to be handed over explicitly. Same Google Fonts
            // URL that `public/index.html` already loads, so no new dependency.
            fonts: [
              {
                cssSrc:
                  'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap',
              },
            ],
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#4CBC18',
                colorBackground: '#ffffff',
                colorText: '#17270D',
                colorDanger: '#D85263',
                borderRadius: '12px',
                fontFamily: 'Figtree, system-ui, sans-serif',
                spacingUnit: '4px',
              },
              rules: {
                '.Input': {
                  border: '1px solid #cbdcc3',
                  boxShadow: 'none',
                },
                // D4.2.2 — placeholders were Stripe's default grey.
                '.Input::placeholder': {
                  color: '#8A9A85',
                },
                // D4.2.3 — hover was the near-black #17270D; focus is the same green but
                // without the glow ring, which he asked to drop.
                '.Input:hover': {
                  border: '1px solid #4CBC18',
                },
                '.Input:focus': {
                  border: '1px solid #4CBC18',
                  boxShadow: 'none',
                },
              },
            },
          }}
        >
          <PaymentInner
            plan={plan}
            planId={planId}
            billing={billing}
            setBilling={setBilling}
            annualSavingPct={annualSavingPct}
            foundingEligible={foundingEligible}
            renewsOn={renewsOn}
            dueToday={dueToday}
            discount={discount}
            discountPct={discountPct}
            listPrice={listPrice}
            intentType={intentType}
          />
        </Elements>
      )}
    </div>
  )
}

export default Payment
